type SavedScrollLockStyles = {
  htmlOverflow: string;
  bodyOverflow: string;
  bodyPosition: string;
  bodyTop: string;
  bodyWidth: string;
  bodyPaddingRight: string;
};

let lockCount = 0;
let savedScrollY = 0;
let savedStyles: SavedScrollLockStyles | null = null;

function clearAppliedLockStyles(html: HTMLElement, body: HTMLElement): void {
  html.style.removeProperty("overflow");
  body.style.removeProperty("overflow");
  body.style.removeProperty("position");
  body.style.removeProperty("top");
  body.style.removeProperty("width");
  body.style.removeProperty("padding-right");
  body.removeAttribute("data-cw-v2-scroll-locked");
}

function restorePreLockStyles(
  html: HTMLElement,
  body: HTMLElement,
  styles: SavedScrollLockStyles,
): void {
  html.style.overflow = styles.htmlOverflow;
  body.style.overflow = styles.bodyOverflow;

  // Never restore `position: fixed` — that is almost always our own stale lock.
  if (styles.bodyPosition && styles.bodyPosition !== "fixed") {
    body.style.position = styles.bodyPosition;
  } else {
    body.style.removeProperty("position");
  }

  if (styles.bodyTop && styles.bodyPosition !== "fixed") {
    body.style.top = styles.bodyTop;
  } else {
    body.style.removeProperty("top");
  }

  if (styles.bodyWidth) {
    body.style.width = styles.bodyWidth;
  } else {
    body.style.removeProperty("width");
  }

  if (styles.bodyPaddingRight) {
    body.style.paddingRight = styles.bodyPaddingRight;
  } else {
    body.style.removeProperty("padding-right");
  }
}

function readScrollYFromFixedBody(body: HTMLElement): number {
  const topValue = body.style.top;
  if (!topValue) return window.scrollY;
  const parsed = Number.parseInt(topValue, 10);
  return Number.isNaN(parsed) ? window.scrollY : Math.abs(parsed);
}

/** Prevent the page behind a modal surface from scrolling. Ref-counted for nested sheets. */
export function lockBodyScroll(): void {
  if (typeof document === "undefined") return;

  const html = document.documentElement;
  const body = document.body;

  if (lockCount === 0) {
    // Recover from a stale lock if a prior surface closed without releasing.
    if (
      body.getAttribute("data-cw-v2-scroll-locked") === "true" ||
      body.style.position === "fixed"
    ) {
      clearAppliedLockStyles(html, body);
    }

    savedScrollY = readScrollYFromFixedBody(body);
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    savedStyles = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyPaddingRight: body.style.paddingRight,
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${savedScrollY}px`;
    body.style.width = "100%";

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    body.setAttribute("data-cw-v2-scroll-locked", "true");
  }

  lockCount += 1;
}

export function unlockBodyScroll(): void {
  if (typeof document === "undefined") return;
  if (lockCount === 0) return;

  lockCount -= 1;
  if (lockCount > 0) return;

  const html = document.documentElement;
  const body = document.body;
  const scrollY = savedScrollY;

  if (savedStyles) {
    restorePreLockStyles(html, body, savedStyles);
    savedStyles = null;
  } else {
    clearAppliedLockStyles(html, body);
  }

  body.removeAttribute("data-cw-v2-scroll-locked");
  window.scrollTo(0, scrollY);
}

/** Clear every scroll-lock attribute/style we or Radix may apply. */
function clearAllScrollLockStyles(html: HTMLElement, body: HTMLElement): void {
  clearAppliedLockStyles(html, body);
  body.removeAttribute("data-scroll-locked");
  body.style.removeProperty("pointer-events");
}

function hasOpenModalSurface(): boolean {
  return Boolean(
    document.querySelector(
      '.cw-v2-sheet__content[data-state="open"], .cw-v2-dialog__content[data-state="open"]',
    ),
  );
}

function runScrollLockRelease(): void {
  if (typeof document === "undefined") return;
  if (hasOpenModalSurface()) return;

  const html = document.documentElement;
  const body = document.body;
  const hadCustomLock =
    savedStyles !== null || body.getAttribute("data-cw-v2-scroll-locked") === "true";
  const scrollY = hadCustomLock ? savedScrollY : window.scrollY;

  lockCount = 0;

  if (savedStyles) {
    restorePreLockStyles(html, body, savedStyles);
    savedStyles = null;
  } else {
    clearAllScrollLockStyles(html, body);
  }

  if (hadCustomLock) {
    window.scrollTo(0, scrollY);
  }
}

/** Release our scroll lock when no V2 sheet/dialog remains open. */
export function releaseBodyScrollLockIfNoModalOpen(): void {
  runScrollLockRelease();
  requestAnimationFrame(runScrollLockRelease);
  window.setTimeout(runScrollLockRelease, 0);
}

/** Test helper — reset module state between tests. */
export function resetBodyScrollLockForTests(): void {
  lockCount = 0;
  savedScrollY = 0;
  savedStyles = null;
}
